import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import authAPI from "../apis/auth/authAPI";

const api = new authAPI();

// The real session lives in HttpOnly cookies that JS cannot read.
// This flag only tells the UI "a login succeeded". It holds no token, and the
// backend stays the source of truth: any 401 clears it.
const SESSION_KEY = "auth.session";
const LOGIN_PATH = "/auth/login";

const readSession = () => {
  try {
    const raw =
      localStorage.getItem(SESSION_KEY) ?? sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const clearStoredSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable */
  }
};

// "Rester connecté" → localStorage (survives closing the browser),
// otherwise sessionStorage (cleared when the tab closes).
const writeSession = (session, remember) => {
  try {
    clearStoredSession();
    (remember ? localStorage : sessionStorage).setItem(
      SESSION_KEY,
      JSON.stringify(session),
    );
  } catch {
    /* storage unavailable */
  }
};


axios.defaults.withCredentials = true;
// Registered once at module load, so it also catches requests fired by
// child components on their very first render.
let handleUnauthorized = () => {};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // The backend renews the session silently, so a 401 means the session
    // is really over. Ignore 401s from the login call itself (wrong password).
    if (status === 401 && !url.includes(LOGIN_PATH)) handleUnauthorized();
    return Promise.reject(error);
  },
);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(readSession);

  const clearSession = useCallback(() => {
    clearStoredSession();
    setSession(null);
  }, []);

  const login = useCallback(async ({ email, password, remember }) => {
    const res = await api.loginApi({ email, password });
    const next = { user: res.data?.user ?? { email }, loggedInAt: Date.now() };
    writeSession(next, remember);
    setSession(next);
    return res;
  }, []);
 const logout = useCallback(async () => {
    try {
      await api.logoutApi();
    } catch (err) {
      console.warn("Logout request failed, clearing local session anyway", err);
    } finally {
      clearSession();
    }
  }, [clearSession]);
  useEffect(() => {
    handleUnauthorized = clearSession;
    return () => {
      handleUnauthorized = () => {};
    };
  }, [clearSession]);

  // Keep several open tabs in sync (login/expiry in one tab updates the others).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SESSION_KEY || e.key === null) setSession(readSession());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

   const value = useMemo(
    () => ({
      user: session?.user ?? null,
      isAuthenticated: Boolean(session),
      login,
      logout,
      clearSession,
    }),
    [session, login, logout, clearSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
